import { PayloadErrand, PayloadThread } from '../../payload/payload-types.zod'
import { UIDataTypes, UIMessage, UITools } from 'ai'
import z from 'zod'
import { NoteResponseSchema, noteToResponse } from './notes'
import { getObject } from '../../payload/payload.helpers'

const ThreadMessageSchema = PayloadThread.shape.messages.element

export const ThreadResponseSchema = PayloadThread.omit({
  individual: true,
  user: true,
  messages: true,
  note: true,
  // Internal bookkeeping for background generation — stays off the wire.
  generatedMessageCount: true,
}).extend({
  messages: z.array(
    ThreadMessageSchema.omit({
      usage: true,
      metadata: true,
      messageId: true,
    }).extend({
      // Lifted out of the message's internal `metadata` (which stays off the wire) so the chat
      // can mark messages the assistant wrote on its own initiative rather than in reply.
      automated: z.boolean().optional(),
      // The documents an automated message was written about, for the pills under it. Only the
      // ones that message reacted to — not everything the errand has.
      sourceDocuments: z
        .array(z.object({ id: z.string(), name: z.string() }))
        .optional(),
      // The same, for notes. Clicking one of these pills opens the note itself, so unlike
      // `sourceDocuments` the id has to still resolve on the errand for the pill to be live.
      sourceNotes: z.array(z.object({ id: z.string(), title: z.string() })).optional(),
      // When the reaction was written. Lets the errand timeline anchor an event to the message
      // that reacted to it — the first reaction at or after the event's own timestamp.
      automatedAt: z.string().optional(),
      // Set only when this reaction covers a status transition, so the chat can show "Status
      // ändrad från X till Y" instead of a generic label.
      statusFrom: PayloadErrand.shape.status.optional(),
      statusTo: PayloadErrand.shape.status.optional(),
    }),
  ),
  note: NoteResponseSchema.optional(),
})

export const ThreadListResponseSchema = ThreadResponseSchema.omit({ messages: true })
  .extend({
    /** @deprecated Use `title`, which falls back to this when nothing was generated yet. */
    firstUserMessage: z.string(),
    /** @deprecated Use `summary`, which falls back to this when nothing was generated yet. */
    lastAssistantMessage: z.string(),
    totalMessages: z.number(),
  })
  .array()

export type PayloadThread = z.infer<typeof PayloadThread>

export type Thread = Omit<z.infer<typeof PayloadThread>, 'messages'> & {
  messages: UIMessage<unknown, UIDataTypes, UITools>[]
}

export type ThreadResponse = z.infer<typeof ThreadResponseSchema>
export type ThreadListResponse = z.infer<typeof ThreadListResponseSchema>

export const threadToResponse = (data: Thread): ThreadResponse => {
  const messages = data.messages.map(message => {
    const { usage, metadata, messageId, ...rest } = message as PayloadThread['messages'][number]
    // Prefer the explicitly-stored AI SDK id (`messageId`) as the message id the frontend renders,
    // so anchoring targets (errand action `sourceMessageId`) still match after a reload.
    // Only the reader-facing half of the reaction metadata is lifted. The watermarks
    // (`documentIds`, `noteIds`) stay off the wire deliberately: they grow to cover everything
    // ever seen, which is not what belongs next to one message.
    const reaction = metadata as
      | {
          at?: string
          automated?: boolean
          sourceDocuments?: { id: string; name: string }[]
          sourceNotes?: { id: string; title: string }[]
          statusFrom?: z.infer<typeof PayloadErrand>['status']
          statusTo?: z.infer<typeof PayloadErrand>['status']
        }
      | undefined

    return {
      ...rest,
      id: messageId ?? rest.id,
      automated: reaction?.automated ? true : undefined,
      automatedAt: reaction?.automated ? reaction.at : undefined,
      sourceDocuments: reaction?.automated ? reaction.sourceDocuments : undefined,
      sourceNotes: reaction?.automated ? reaction.sourceNotes : undefined,
      statusFrom: reaction?.automated ? reaction.statusFrom : undefined,
      statusTo: reaction?.automated ? reaction.statusTo : undefined,
    }
  })

  const note = data.note ? getObject(data.note) : undefined

  return ThreadResponseSchema.parse({
    createdAt: data.createdAt,
    id: data.id,
    messages: messages,
    updatedAt: data.updatedAt,
    // Payload stores unset text fields as null, the schema expects optional.
    title: data.title ?? undefined,
    summary: data.summary ?? undefined,
    note: note ? noteToResponse(note) : undefined,
    type: data.type,
  } satisfies ThreadResponse)
}

export const threadListToResponse = (data: Thread[]): ThreadListResponse => {
  return ThreadListResponseSchema.parse(
    data.map(thread => {
      const userMessages = thread.messages.filter(message => message.role === 'user')
      const firstUserMessages = userMessages[0]
      const firstUserContent = firstUserMessages
        ? firstUserMessages.parts
            .filter(part => part.type === 'text')
            .map(part => part.text)
            .join(' ')
        : ''

      const assistantMessages = thread.messages.filter(message => message.role === 'assistant')
      const lastAssistantMessages = assistantMessages[assistantMessages.length - 1]
      const lastAssitantContent = lastAssistantMessages
        ? lastAssistantMessages.parts
            .filter(part => part.type === 'text')
            .map(part => part.text)
            .join(' ')
        : ''

      return {
        createdAt: thread.createdAt,
        id: thread.id,
        updatedAt: thread.updatedAt,
        // Fall back to the derived message text when nothing has been generated yet,
        // so pre-existing threads keep rendering exactly as they did before.
        title: thread.title || firstUserContent || undefined,
        summary: thread.summary || lastAssitantContent || undefined,
        firstUserMessage: firstUserContent,
        lastAssistantMessage: lastAssitantContent,
        totalMessages: thread.messages.length,
        type: thread.type,
      } satisfies ThreadListResponse[number]
    }),
  )
}
